import { useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { EmojiPicker } from './EmojiPicker';
import type { MessageReaction } from '../types';

interface AggregatedReaction {
  emoji: string;
  count: number;
  mine: boolean;
}

function aggregate(reactions: MessageReaction[], myUserId: string | null): AggregatedReaction[] {
  const map = new Map<string, AggregatedReaction>();
  for (const r of reactions) {
    const existing = map.get(r.emoji) ?? { emoji: r.emoji, count: 0, mine: false };
    existing.count += 1;
    if (myUserId && r.userId === myUserId) existing.mine = true;
    map.set(r.emoji, existing);
  }
  return [...map.values()];
}

interface BaseProps {
  reactions: MessageReaction[];
  myUserId: string | null;
  outbound: boolean;
  /** Emoji setzen oder (wenn bereits die eigene Reaktion) entfernen. */
  onToggle: (emoji: string, currentlyMine: boolean) => void;
}

/**
 * „Reagieren"-Auslöser einer Nachricht — WhatsApp-Stil, REFLOW-FREI & ZUVERLÄSSIG KLICKBAR.
 *
 * Der Knopf ist ein absolut positioniertes Overlay, das DIREKT an der Außenkante der Bubble
 * klebt (right-full/left-full + Padding-Brücke) — also OHNE toten Spalt dazwischen. Dadurch
 * kann die Maus von der Bubble auf den Knopf wandern, ohne eine Nicht-Hover-Zone zu kreuzen.
 *
 * Sichtbarkeit läuft über reines CSS `group-hover`/`focus-within` (KEIN JS-mouseleave, das beim
 * Verlassen der Bubble-Geometrie zu früh feuert). Da der Knopf ein DOM-Nachfahre des `group`-
 * Wrappers ist, bleibt `group-hover` aktiv, solange die Maus über dem Knopf steht → er
 * verschwindet nicht mehr, bevor man ihn trifft. Bei offenem Picker bleibt er erzwungen sichtbar.
 *
 * Absolut positioniert → kein Platz im Fluss → die Bubble-Höhe/-Position ändert sich beim
 * Hovern NIE.
 *
 * MUSS innerhalb eines Elements mit Klasse `group` und `relative` gerendert werden.
 */
export function ReactionTrigger({ reactions, myUserId, outbound, onToggle }: BaseProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const aggregated = aggregate(reactions, myUserId);

  return (
    <div
      className={[
        'absolute top-1/2 z-20 -translate-y-1/2',
        // Klebt an der Bubble-Außenkante; das Padding bildet eine lückenlose Hover-/Klick-Brücke.
        outbound ? 'right-full pr-1' : 'left-full pl-1',
        'transition-opacity duration-150',
        // Sichtbar bei Hover/Fokus der Nachricht; bei offenem Picker erzwungen sichtbar.
        pickerOpen
          ? 'opacity-100'
          : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto',
      ].join(' ')}
    >
      <button
        ref={btnRef}
        type="button"
        // preventDefault verhindert, dass der Klick den Fokus auf den (ggf. randnahen) Knopf
        // legt und der Scrollcontainer ihn „in den Blick" scrollt → kein Springen beim Öffnen.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setPickerOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        title="Reagieren"
        aria-label="Auf Nachricht reagieren"
        className={[
          'inline-flex h-7 w-7 items-center justify-center rounded-pill border bg-white shadow-brand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40',
          pickerOpen
            ? 'border-kalkuel-accent text-kalkuel-primary'
            : 'border-kalkuel-border text-kalkuel-muted hover:border-kalkuel-accent hover:text-kalkuel-primary',
        ].join(' ')}
      >
        <SmilePlus size={14} />
      </button>

      {pickerOpen && (
        <EmojiPicker
          anchorRef={btnRef}
          onPick={(emoji) => {
            const mine = aggregated.find((r) => r.emoji === emoji)?.mine ?? false;
            onToggle(emoji, mine);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Vorhandene Reaktions-Chips (mit Zähler) unter der Bubble. Rendert NUR, wenn es Reaktionen
 * gibt — ist also reine Inhaltsanzeige (kein Hover-Element) und verursacht damit keinen
 * Hover-Reflow. Klick auf die eigene Reaktion entfernt sie (Toggle).
 */
export function ReactionChips({ reactions, myUserId, outbound, onToggle }: BaseProps) {
  const aggregated = aggregate(reactions, myUserId);
  if (aggregated.length === 0) return null;

  return (
    <div
      className={[
        'mt-1 flex flex-wrap items-center gap-1',
        outbound ? 'justify-end' : 'justify-start',
      ].join(' ')}
    >
      {aggregated.map((r) => (
        <button
          key={r.emoji}
          type="button"
          onClick={() => onToggle(r.emoji, r.mine)}
          aria-pressed={r.mine}
          title={r.mine ? 'Eigene Reaktion entfernen' : 'Mit diesem Emoji reagieren'}
          className={[
            'inline-flex items-center gap-1 rounded-pill border px-1.5 py-0.5 text-[11px] leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40',
            r.mine
              ? 'border-kalkuel-accent bg-kalkuel-accent-soft text-kalkuel-primary'
              : 'border-kalkuel-border bg-white text-kalkuel-muted hover:border-kalkuel-accent',
          ].join(' ')}
        >
          <span className="leading-none">{r.emoji}</span>
          {r.count > 1 && <span className="tabular-nums">{r.count}</span>}
        </button>
      ))}
    </div>
  );
}
